import { Box, Grid, MenuItem, Typography } from '@mui/material'
import includes from 'lodash/includes'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import moment from 'moment'
import { useCallback, useEffect, useState } from 'react'

import {
  ArcElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js'
import { Doughnut } from 'react-chartjs-2'

/** TRANSLATION */
import { useTranslation } from 'react-i18next'

/** CONSTANTS */
import { colors } from 'constants/theme'

/** COMPONENT */
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker'
import FilterSelect from 'component/Select/FilterSelect'
import { CardSalesProps, handleChangeFilterSelect, renderFilterSelect } from 'features/dashboard/dashboard'

/** UTILS || SLICE */
import { getSecureItem, numberFormat } from 'utils/app.utils'

/** API */
import { BranchInterface } from 'api/master/clinic.api'
import { SelectChangeEvent } from '@mui/material'

/** STYLE */
import DashboardApi, { IFPaymentChannel, IFSumeryFilter } from 'api/dashboard.api'
import { TooltipItem } from 'chart.js'
import { KEY_STORAGE } from 'constants/common'
import * as UseStyled from 'features/dashboard/useStyled'

const bg = colors.dashboard.paymentMethod

export default function PaymentMethod(props: CardSalesProps) {
  const { t } = useTranslation()

  const PAYMENT_CHANNELS: Record<number, string> = { 1: 'CASH', 2: 'TRANSFER', 3: 'CREDIT', 4: 'DEBIT', 5: 'TREATMENT_RIGHT', 6: 'ADVANCE', 7: 'VOUCHER', 8: 'PROMOTION' }

  const [data, setData] = useState<{ labels: string[]; datasets: { data: number[]; spacing: number; backgroundColor: string[]; hoverBackgroundColor: string[]; borderWidth: number; cutout: string }[] }>({
    labels: [],
    datasets: []
  })
  const [lists, setLists] = useState<IFPaymentChannel[]>([])

  const [loadFirst, setLoadFirst] = useState(false);
  const dashboardMain = getSecureItem(KEY_STORAGE.DASHBOARD)
  const dashboardCurrent = dashboardMain ? JSON.parse(dashboardMain) : {}

  const [count, setCount] = useState<number>(0)
  const [branches, setBranches] = useState<BranchInterface[]>([])
  const [branch, setBranch] = useState<(string | number)[]>(dashboardCurrent?.paymentMethod?.branch || ['ALL'])
  const [rangeDate, setRangeDate] = useState(dashboardCurrent?.paymentMethod?.rangeDate || [moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')])
  const [summary, setSummary] = useState<IFPaymentChannel[]>([]);

  const handleChangeSelectBranch = (e: SelectChangeEvent<(string | number)[]>) => {
    setBranch(handleChangeFilterSelect(e))
  }

  useEffect(() => {
    if (!loadFirst) {
      const timer = setTimeout(() => {
        setLoadFirst(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [props.rangeDate, props.branches]);

  useEffect(() => {
    setBranches(props.branches || [])
  }, [props.branches])

  useEffect(() => {
    loadFirst && setRangeDate(props.rangeDate || [])
  }, [props.rangeDate])

  useEffect(() => {
    loadFirst && setBranch(props.branch || [])
  }, [props.branch])

  useEffect(() => {
    props?.handleChangeDashboard && props?.handleChangeDashboard("paymentMethod,branch", branch)
  }, [branch])

  useEffect(() => {
    props?.handleChangeDashboard && props?.handleChangeDashboard("paymentMethod,rangeDate", rangeDate)
  }, [rangeDate])

  const loadData = useCallback(async () => {
    const condition: IFSumeryFilter = { dateStart: rangeDate[0], dateEnd: rangeDate[1], branchId: branch[0] === 'ALL' ? `` : branch.toString() }
    const res = await DashboardApi.findTopPaymentChannel(condition)
    const labels: string[] = []
    const newLists: IFPaymentChannel[] = []
    const sumAmountPaid: number[] = []
    setCount(0)
    setLists([])
    if (res.status === 200) {
      const summeris: IFPaymentChannel[] = res.data
      setSummary(summeris)

      for (const x of summeris) {
        if (x.paymentChannelId) {
          setCount((values: number) => values + x.countPaymentchannel)
          labels.push(t(`DASHBOARD.PAYMENT_CHART.PAYMENT_METHODS.${PAYMENT_CHANNELS[x.paymentChannelId]}`))
          sumAmountPaid.push(x.sumAmountPaid)
          newLists.push(x)
        }
      }
    }
    setLists(newLists)
    setData({
      labels,
      datasets: [
        {
          data: sumAmountPaid,
          spacing: !isEmpty(sumAmountPaid) ? 1 : 0,
          backgroundColor: bg,
          hoverBackgroundColor: bg,
          borderWidth: !isEmpty(sumAmountPaid) ? 1 : 0,
          cutout: '75%',
        }
      ]
    })
  }, [branch, rangeDate])

  useEffect(() => {
    loadData()
  }, [loadData])
  const options = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context: TooltipItem<'doughnut'>) {
            return ` ${context.label}: ${numberFormat(context.raw as number)}`
          }
        }
      },
    },
  }

  return (
    <UseStyled.Card>
      <Box className='pt-3 px-3 px-xl-4' sx={{ borderBottom: `1px solid ${colors.lightGray}`, paddingBottom: '12px' }}>
        <Grid container spacing={1} alignItems={'center'}>
          <Grid item xs={12} md={4} lg={12} xl={4} className={'w-100'}>
            <UseStyled.Title type='title' sx={{ flex: 'none' }}>{t('DASHBOARD.PAYMENT_METHODS')}</UseStyled.Title>
          </Grid>
          <Grid item xs={12} sm={4} md={4} lg={6} xl={4} className={'w-100 pr-sm-2'}>
            <FilterSelect
              multiple
              label={t('')}
              style={{ height: '40px' }}
              labelId="label-branch"
              selectId="select-branch"
              onchange={handleChangeSelectBranch}
              renderValue={() => `${t('DASHBOARD.FILTER.BRANCH')}: ${renderFilterSelect('branch', branches, branch) === 'ALL' ? t('STATUS.ALL') : renderFilterSelect('branch', branches, branch)}`}
              value={branch}
              options={[
                <MenuItem
                  key="ALL"
                  value="ALL"
                  sx={{
                    '&.MuiMenuItem-root.Mui-selected': {
                      pointerEvents: includes(branch, 'ALL') ? `none` : 'auto',
                      backgroundColor: includes(branch, 'ALL') ? `${colors.themeSecondColor06} !important` : 'transparent',
                      color: includes(branch, 'ALL') ? `${colors.themeSecondColor} !important` : `${colors.textPrimary}`
                    }
                  }}>
                  {t('STATUS.ALL')}
                </MenuItem>,
                map(props.branches, (val: BranchInterface, indexBranch: number) => {
                  return (
                    <MenuItem
                      key={indexBranch}
                      value={val.branchId}
                      sx={{
                        '&.MuiMenuItem-root.Mui-selected': {
                          backgroundColor: includes(branch, val.branchId) ? `${colors.themeSecondColor06} !important` : 'transparent',
                          color: includes(branch, val.branchId) ? `${colors.themeSecondColor} !important` : `${colors.textPrimary}`
                        }
                      }}>
                      {val.branchName}
                    </MenuItem>
                  )
                })
              ]}
              formControlClass={'not-max-width'}
              classesOption="style-select-doctors"
            />
          </Grid>
          <Grid item xs={12} sm={4} md={4} lg={6} xl={4} className={'w-100'}>
            <InputSecondNewRangePicker
              inputHeight={40}
              value={rangeDate}
              onchange={(val) => {
                if (isEmpty(val)) setRangeDate(emptyRangePicker)
                else setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
              }}
              allowClear
              onClear={() => setRangeDate(emptyRangePicker)}
            />
          </Grid>
        </Grid>
      </Box>
      <Box className='py-3 pt-xl-4 pb-1 px-3 px-xl-4'>
        <Grid container spacing={1} alignItems={!isEmpty(summary) ? 'center' : 'start'}>
          <Grid item xs={5} sm={4} lg={6} xl={4} className="w-100">
            <UseStyled.DoughnutChart type='payment' className='mx-auto ml-sm-0 position-relative'>
              <Doughnut options={options} data={data} />
              {isEmpty(summary) && <UseStyled.EmptyDoughnutChart /> || <></>}
              <UseStyled.DoughnutDataInside className='text-center position-absolute'>
                <Typography>{t('DASHBOARD.PAYMENT_CHART.PAYMENT_COUNT', { qty: numberFormat(count, 0, 0) })}</Typography>
                <Typography sx={{ fontSize: '14px', color: `${colors.textExtraLightGray} !important` }}>{t('DASHBOARD.PAYMENT_CHART.PAYMENT')}</Typography>
              </UseStyled.DoughnutDataInside>
            </UseStyled.DoughnutChart>
          </Grid>
          <Grid item xs={7} sm={8} lg={6} xl={8} className="w-100">
            <Typography className='mt-3 mt-sm-0 mb-2' sx={{ fontWeight: 500 }}>{t('DASHBOARD.PAYMENT_CHART.PAYMENT_METHODS.TITLE')}</Typography>
            <Grid container spacing={1}>
              {!isEmpty(summary) ? (
                map(lists, (list: IFPaymentChannel, i: number) => {
                  return (
                    <Grid item xs={6} lg={12} xl={6} className="w-100">
                      <Box className='d-flex align-items-center'>
                        <UseStyled.LabelColor color={bg[i]} />
                        <Typography className='ml-2' sx={{ fontSize: '18px', fontWeight: 500 }}>{numberFormat(list.sumAmountPaid)}</Typography>
                      </Box>
                      <Typography sx={{ color: `${colors.textExtraLightGray} !important` }}>
                        {t(`DASHBOARD.PAYMENT_CHART.PAYMENT_METHODS.${PAYMENT_CHANNELS[list.paymentChannelId]}`)}({t('DASHBOARD.PAYMENT_CHART.PAYMENT_COUNT', { qty: numberFormat(list.countPaymentchannel, 0, 0) })})
                      </Typography>
                    </Grid>
                  )
                })
              ) : (
                <Grid item xs={12} className="w-100 py-3 py-lg-5">
                  <Box className='text-center'>{t('NOT_FOUND')}</Box>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </UseStyled.Card>
  )
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  ArcElement,
  Title,
  Tooltip,
  Legend
)
